import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import {
  computed,
  Inject,
  Injectable,
  PLATFORM_ID,
  signal,
} from "@angular/core";

export type AppPalette = "signal-blue" | "violet" | "teal";
export type AppFontSize = "compact" | "default" | "large";

export interface AppearanceOption<T extends string> {
  id: T;
  label: string;
  description: string;
  swatch?: string;
}

export const APP_PALETTES: readonly AppearanceOption<AppPalette>[] = [
  {
    id: "signal-blue",
    label: "Signal blue",
    description: "Clear blue for operational focus",
    swatch: "#005cbb",
  },
  {
    id: "violet",
    label: "Studio violet",
    description: "Violet with a warmer editorial accent",
    swatch: "#7d00fa",
  },
  {
    id: "teal",
    label: "Scanner teal",
    description: "Teal for a lower-glare workspace",
    swatch: "#006a6a",
  },
];

export const APP_FONT_SIZES: readonly AppearanceOption<AppFontSize>[] = [
  {
    id: "compact",
    label: "Compact",
    description: "15 px base text",
  },
  {
    id: "default",
    label: "Default",
    description: "16 px base text",
  },
  {
    id: "large",
    label: "Large",
    description: "18 px base text",
  },
];

interface AppearancePreferences {
  palette: AppPalette;
  fontSize: AppFontSize;
}

const STORAGE_KEY = "bnn-admin.appearance";
const DEFAULT_PREFERENCES: AppearancePreferences = {
  palette: "signal-blue",
  fontSize: "default",
};

@Injectable({ providedIn: "root" })
export class AppearanceService {
  readonly palette = signal<AppPalette>(DEFAULT_PREFERENCES.palette);
  readonly fontSize = signal<AppFontSize>(DEFAULT_PREFERENCES.fontSize);
  readonly isDefault = computed(
    () =>
      this.palette() === DEFAULT_PREFERENCES.palette &&
      this.fontSize() === DEFAULT_PREFERENCES.fontSize,
  );

  private initialized = false;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {}

  initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    const stored = this.readStoredPreferences();
    this.palette.set(stored.palette);
    this.fontSize.set(stored.fontSize);
    this.apply(stored);
  }

  setPalette(palette: AppPalette): void {
    this.initialize();
    this.palette.set(palette);
    this.saveAndApply();
  }

  setFontSize(fontSize: AppFontSize): void {
    this.initialize();
    this.fontSize.set(fontSize);
    this.saveAndApply();
  }

  reset(): void {
    this.palette.set(DEFAULT_PREFERENCES.palette);
    this.fontSize.set(DEFAULT_PREFERENCES.fontSize);

    if (isPlatformBrowser(this.platformId)) {
      try {
        this.document.defaultView?.localStorage.removeItem(STORAGE_KEY);
      } catch {
        // Storage can be unavailable in private or restricted browser contexts.
      }
    }

    this.apply(DEFAULT_PREFERENCES);
  }

  private saveAndApply(): void {
    const preferences: AppearancePreferences = {
      palette: this.palette(),
      fontSize: this.fontSize(),
    };

    if (isPlatformBrowser(this.platformId)) {
      try {
        this.document.defaultView?.localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify(preferences),
        );
      } catch {
        // The visual preference still applies for the current session.
      }
    }

    this.apply(preferences);
  }

  private readStoredPreferences(): AppearancePreferences {
    if (!isPlatformBrowser(this.platformId)) {
      return DEFAULT_PREFERENCES;
    }

    try {
      const value =
        this.document.defaultView?.localStorage.getItem(STORAGE_KEY);
      if (!value) {
        return DEFAULT_PREFERENCES;
      }

      const parsed = JSON.parse(value) as Partial<AppearancePreferences>;
      return {
        palette: APP_PALETTES.some((option) => option.id === parsed.palette)
          ? (parsed.palette as AppPalette)
          : DEFAULT_PREFERENCES.palette,
        fontSize: APP_FONT_SIZES.some((option) => option.id === parsed.fontSize)
          ? (parsed.fontSize as AppFontSize)
          : DEFAULT_PREFERENCES.fontSize,
      };
    } catch {
      return DEFAULT_PREFERENCES;
    }
  }

  private apply(preferences: AppearancePreferences): void {
    const root = this.document.documentElement;
    root.dataset["appPalette"] = preferences.palette;
    root.dataset["appFontSize"] = preferences.fontSize;
  }
}
